/**
 * Stripe Billing Configuration
 * ============================
 * Controls test/live mode switching for Stripe payments.
 *
 * Configuration is controlled via environment variable:
 * - Set VITE_STRIPE_TEST_MODE=true for test mode (development)
 * - Set VITE_STRIPE_TEST_MODE=false for live mode (production)
 *
 * This ensures each developer can safely test payments without affecting production.
 *
 * Membership Tiers:
 * - FREE: $0 - Public video browsing only
 * - BASIC: $25/mo - 1 coach seat, assign public videos, 30-day client access
 * - ADVANCED: $45/mo - 1 coach seat, upload own videos, 90-day client access
 * - CORPORATE: $199/mo - Up to 10 coaches, advanced features
 * - PLATINUM: Custom - Enterprise features (contact sales)
 *
 * @see https://stripe.com/docs
 */

export interface StripeConfig {
  publishableKey: string
  isTestMode: boolean
}

export type TierKey = 'FREE' | 'BASIC' | 'ADVANCED' | 'CORPORATE' | 'PLATINUM'
export type TierId = 'free' | 'basic' | 'advanced' | 'corporate' | 'platinum'
export type PaidTierId = 'basic' | 'advanced' | 'corporate'

export interface TierLimits {
  coachSeats: number
  canUpload: boolean
  clientAccessDays: number
  maxClients: number
}

export interface Tier {
  id: TierId
  name: string
  price: number | null
  features: string[]
  limits: TierLimits
}

/**
 * Check if a Stripe key looks like a live key
 */
function isLiveKey(key: string): boolean {
  return key.startsWith('pk_live_') || key.startsWith('sk_live_')
}

/**
 * Check if a Stripe key looks like a test key
 */
function isTestKey(key: string): boolean {
  return key.startsWith('pk_test_') || key.startsWith('sk_test_')
}

export const STRIPE_CONFIG = {
  /**
   * Toggle between test and live Stripe keys
   * Controlled by VITE_STRIPE_TEST_MODE environment variable
   * Default: true (use test mode for safety)
   */
  USE_TEST_MODE:
    import.meta.env.VITE_STRIPE_TEST_MODE === 'true' ||
    import.meta.env.VITE_STRIPE_TEST_MODE === true ||
    !import.meta.env.VITE_STRIPE_TEST_MODE, // Default to test mode if not set

  // Test mode publishable key
  TEST_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_TEST_PUBLISHABLE_KEY || '',

  // Live mode publishable key
  LIVE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_LIVE_PUBLISHABLE_KEY || '',

  // Price IDs from environment (same IDs work in both test and live mode)
  PRICE_IDS: {
    basic: import.meta.env.VITE_STRIPE_PRICE_BASIC || '',
    advanced: import.meta.env.VITE_STRIPE_PRICE_ADVANCED || '',
    corporate: import.meta.env.VITE_STRIPE_PRICE_CORPORATE || '',
  } as Record<PaidTierId, string>,

  // Subscription tier configuration
  tiers: {
    FREE: {
      id: 'free',
      name: 'Free',
      price: 0,
      features: [
        'Browse public video library',
        'Watch unlimited public videos',
        'Like and bookmark videos',
      ],
      limits: {
        coachSeats: 0,
        canUpload: false,
        clientAccessDays: 0,
        maxClients: 0,
      },
    },
    BASIC: {
      id: 'basic',
      name: 'Basic',
      price: 25,
      features: [
        'Everything in Free',
        '1 coach seat',
        'Assign public videos to clients',
        '30-day client access',
        'Basic completion tracking',
      ],
      limits: {
        coachSeats: 1,
        canUpload: false,
        clientAccessDays: 30,
        maxClients: 50,
      },
    },
    ADVANCED: {
      id: 'advanced',
      name: 'Advanced',
      price: 45,
      features: [
        'Everything in Basic',
        'Upload your own videos',
        'Public or private video visibility',
        '90-day client access',
        'Custom playlists',
        'Advanced analytics',
      ],
      limits: {
        coachSeats: 1,
        canUpload: true,
        clientAccessDays: 90,
        maxClients: 100,
      },
    },
    CORPORATE: {
      id: 'corporate',
      name: 'Corporate',
      price: 199,
      features: [
        'Everything in Advanced',
        'Up to 10 coach seats',
        'Team management',
        'Shared video library',
        'Priority support',
        'Custom branding',
      ],
      limits: {
        coachSeats: 10,
        canUpload: true,
        clientAccessDays: 90,
        maxClients: 500,
      },
    },
    PLATINUM: {
      id: 'platinum',
      name: 'Platinum',
      price: null, // Custom pricing - contact sales
      features: [
        'Everything in Corporate',
        'Unlimited coach seats',
        'Dedicated account manager',
        'Custom integrations',
        'SLA guarantee',
        'White-label options',
      ],
      limits: {
        coachSeats: -1, // Unlimited
        canUpload: true,
        clientAccessDays: 365,
        maxClients: -1, // Unlimited
      },
    },
  } as const satisfies Record<TierKey, Tier>,

  /**
   * Get the current Stripe configuration based on environment settings
   * Returns the appropriate publishable key for test or live mode
   *
   * Safety feature: If test mode is enabled but a live key is detected,
   * it will throw an error to prevent accidental production charges
   */
  getStripeConfig(): StripeConfig {
    if (this.USE_TEST_MODE) {
      // Safety check: Make sure we're not accidentally using live keys in test mode
      if (isLiveKey(this.TEST_PUBLISHABLE_KEY)) {
        console.error(
          '🚨 SAFETY ERROR: VITE_STRIPE_TEST_MODE=true but VITE_STRIPE_TEST_PUBLISHABLE_KEY contains a live key!'
        )
        console.error(
          '   Test publishable key should start with pk_test_, not pk_live_.'
        )
        throw new Error(
          'Invalid configuration: Test mode enabled but live key detected. Check your .env file.'
        )
      }

      console.log('🔧 Using TEST Stripe Keys')
      console.log('=== Stripe Configuration ===')
      console.log(`Mode: TEST`)
      console.log(`Publishable Key: ${this.TEST_PUBLISHABLE_KEY ? '✓ Set' : '✗ Missing'}`)
      console.log('============================')

      return {
        publishableKey: this.TEST_PUBLISHABLE_KEY,
        isTestMode: true,
      }
    }

    // Safety check: Warn if using test keys in live mode
    if (isTestKey(this.LIVE_PUBLISHABLE_KEY)) {
      console.warn(
        '⚠️ WARNING: VITE_STRIPE_TEST_MODE=false but VITE_STRIPE_LIVE_PUBLISHABLE_KEY contains a test key!'
      )
      console.warn(
        '   This may cause issues in production. Live key should start with pk_live_.'
      )
    }

    console.log('🚀 Using LIVE Stripe Keys')
    console.log('=== Stripe Configuration ===')
    console.log(`Mode: LIVE`)
    console.log(`Publishable Key: ${this.LIVE_PUBLISHABLE_KEY ? '✓ Set' : '✗ Missing'}`)
    console.log('============================')

    return {
      publishableKey: this.LIVE_PUBLISHABLE_KEY,
      isTestMode: false,
    }
  },

  /**
   * Get the publishable key for the current mode
   */
  getPublishableKey(): string {
    return this.USE_TEST_MODE
      ? this.TEST_PUBLISHABLE_KEY
      : this.LIVE_PUBLISHABLE_KEY
  },

  /**
   * Get the price ID for a given tier
   * Returns empty string for free tier or platinum (contact sales)
   */
  getPriceId(tierId: TierId): string {
    if (tierId === 'free' || tierId === 'platinum') {
      return ''
    }
    return this.PRICE_IDS[tierId as PaidTierId] || ''
  },

  /**
   * Check if Stripe is properly configured
   */
  isConfigured(): boolean {
    const key = this.getPublishableKey()
    return Boolean(key) && (isTestKey(key) || isLiveKey(key))
  },

  /**
   * Check if price IDs are configured for paid tiers
   */
  hasPriceIds(): boolean {
    return Boolean(
      this.PRICE_IDS.basic &&
      this.PRICE_IDS.advanced &&
      this.PRICE_IDS.corporate
    )
  },

  /**
   * Get tier by ID
   */
  getTier(tierId: TierId): Tier | null {
    const tierKey = Object.keys(this.tiers).find(
      key => this.tiers[key as TierKey].id === tierId
    ) as TierKey | undefined
    return tierKey ? this.tiers[tierKey] : null
  },

  /**
   * Get tier by key
   */
  getTierByKey(tierKey: TierKey): Tier {
    return this.tiers[tierKey]
  },

  /**
   * Check if a feature is available for a tier
   */
  canAccess(tierId: TierId, feature: 'upload' | 'clients' | 'coaches'): boolean {
    const tier = this.getTier(tierId)
    if (!tier) return false

    switch (feature) {
      case 'upload':
        return tier.limits.canUpload
      case 'clients':
        return tier.limits.maxClients > 0 || tier.limits.maxClients === -1
      case 'coaches':
        return tier.limits.coachSeats > 0 || tier.limits.coachSeats === -1
      default:
        return false
    }
  },

  /**
   * Check if we're using test mode
   */
  isTestMode(): boolean {
    return this.USE_TEST_MODE
  },

  /**
   * Validate that required configuration is present
   * Returns true if valid, throws error if invalid
   */
  validate(): boolean {
    const key = this.getPublishableKey()
    const mode = this.USE_TEST_MODE ? 'test' : 'live'

    if (!key) {
      console.warn(
        `⚠️ Missing Stripe publishable key for ${mode} mode. Payments will not work.`
      )
      return false
    }

    if (!this.hasPriceIds()) {
      console.warn(
        `⚠️ Missing Stripe price IDs. Checkout will not work until products are created in Stripe.`
      )
    }

    return true
  },

  /**
   * Log current configuration status (detailed)
   */
  logStatus() {
    console.log('📊 Stripe Configuration Status:')
    console.log(`   Mode: ${this.USE_TEST_MODE ? 'TEST' : 'LIVE'}`)
    console.log(`   Publishable Key: ${this.getPublishableKey() ? '✓ Set' : '✗ Missing'}`)
    console.log(`   Price IDs:`)
    console.log(`      Basic: ${this.PRICE_IDS.basic ? '✓ Set' : '✗ Missing'}`)
    console.log(`      Advanced: ${this.PRICE_IDS.advanced ? '✓ Set' : '✗ Missing'}`)
    console.log(`      Corporate: ${this.PRICE_IDS.corporate ? '✓ Set' : '✗ Missing'}`)
    console.log(`   Tiers: ${Object.keys(this.tiers).length}`)
  },
}

// For backwards compatibility with existing code
export const stripeConfig = STRIPE_CONFIG
